import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import HomePage from "./pages/HomePage";
import AISolutionsPage from "./pages/AISolutionsPage";
import { ServicesOverviewPage, ServiceDetailPage } from "./pages/ServicesPages";
import ServicePageTemplate from "./components/ServicePageTemplate";
import { productEngineering, businessAutomation, erpEcommerce, consulting } from "./data/legacyServicesData";
import {
  DigitalMarketingHub,
  SEOHub,
  AISEOPage,
  LLMOptimizationPage,
  EcommerceSEOPage,
  TechnicalSEOPage,
  SEOCopywritingPage,
  SocialMediaPage,
  PPCPage,
  BrandStrategyPage,
} from "./pages/MarketingPages";
import ApproachPage from "./pages/ApproachPage";
import AboutPage from "./pages/AboutPage";
import { CaseStudiesPage, CaseStudyDetailPage } from "./pages/CaseStudiesPage";
import ResourcesPage from "./pages/ResourcesPage";
import ContactPage from "./pages/ContactPage";
import AdminPage from "./pages/AdminPage";
import IndustryPage from "./pages/IndustryPages";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Admin route without layout */}
          <Route path="/admin" element={<AdminPage />} />
          
          {/* Public routes with layout */}
          <Route path="/" element={<Layout><HomePage /></Layout>} />
          <Route path="/ai-solutions" element={<Layout><AISolutionsPage /></Layout>} />
          <Route path="/ai-solutions/:industryId" element={<Layout><IndustryPage /></Layout>} />
          <Route path="/services" element={<Layout><ServicesOverviewPage /></Layout>} />

          {/* Digital Marketing Tree (specific routes — must come BEFORE /services/:serviceId) */}
          <Route path="/services/digital-marketing" element={<Layout><DigitalMarketingHub /></Layout>} />
          <Route path="/services/digital-marketing/seo" element={<Layout><SEOHub /></Layout>} />
          <Route path="/services/digital-marketing/seo/ai-seo" element={<Layout><AISEOPage /></Layout>} />
          <Route path="/services/digital-marketing/seo/llm-optimization" element={<Layout><LLMOptimizationPage /></Layout>} />
          <Route path="/services/digital-marketing/seo/ecommerce" element={<Layout><EcommerceSEOPage /></Layout>} />
          <Route path="/services/digital-marketing/seo/technical" element={<Layout><TechnicalSEOPage /></Layout>} />
          <Route path="/services/digital-marketing/seo/copywriting" element={<Layout><SEOCopywritingPage /></Layout>} />
          <Route path="/services/digital-marketing/social-media" element={<Layout><SocialMediaPage /></Layout>} />
          <Route path="/services/digital-marketing/ppc" element={<Layout><PPCPage /></Layout>} />
          <Route path="/services/digital-marketing/brand-strategy" element={<Layout><BrandStrategyPage /></Layout>} />

          {/* Legacy 4 service pages migrated to master ServicePageTemplate */}
          <Route path="/services/product-engineering" element={<Layout><ServicePageTemplate data={productEngineering} testIdSlug="product-engineering" /></Layout>} />
          <Route path="/services/business-automation" element={<Layout><ServicePageTemplate data={businessAutomation} testIdSlug="business-automation" /></Layout>} />
          <Route path="/services/erp-ecommerce" element={<Layout><ServicePageTemplate data={erpEcommerce} testIdSlug="erp-ecommerce" /></Layout>} />
          <Route path="/services/consulting" element={<Layout><ServicePageTemplate data={consulting} testIdSlug="consulting" /></Layout>} />

          <Route path="/services/:serviceId" element={<Layout><ServiceDetailPage /></Layout>} />
          <Route path="/approach" element={<Layout><ApproachPage /></Layout>} />
          <Route path="/about" element={<Layout><AboutPage /></Layout>} />
          <Route path="/case-studies" element={<Layout><CaseStudiesPage /></Layout>} />
          <Route path="/case-studies/:id" element={<Layout><CaseStudyDetailPage /></Layout>} />
          <Route path="/resources" element={<Layout><ResourcesPage /></Layout>} />
          <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
          
          {/* Static pages */}
          <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
          <Route path="/terms" element={<Layout><TermsPage /></Layout>} />

          {/* 404 catch-all */}
          <Route path="*" element={
            <Layout>
              <section className="section-padding min-h-[60vh] flex items-center">
                <div className="container-custom text-center">
                  <div className="text-7xl font-bold text-[#2563EB] mb-4">404</div>
                  <h1 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">Page not found</h1>
                  <p className="text-slate-600 mb-8 max-w-md mx-auto">
                    Sorry, we could not find the page you are looking for. It may have moved or no longer exist.
                  </p>
                  <a href="/" className="inline-block bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-3 font-medium">
                    Return home
                  </a>
                </div>
              </section>
            </Layout>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

// Simple static pages
const PrivacyPage = () => (
  <div className="section-padding" data-testid="privacy-page">
    <div className="container-custom max-w-3xl">
      <h1 className="font-outfit text-4xl font-bold text-[#0A2540] mb-8">Privacy Policy</h1>
      <div className="prose prose-slate max-w-none space-y-6 text-slate-600 leading-relaxed">
        <p>
          At Blue Ribbon Technologies, we respect your privacy and are committed to protecting it. We advocate transparency and therefore want to give you a clear overview of how we treat your information that we receive from you via the Internet. This Privacy Policy applies only to our online activities and is valid for visitors to our website with regards to the information that they shared and/or was collected while browsing our website. This policy is not applicable to any information collected offline or via channels other than this website.
        </p>
        <p>
          By using our website, you hereby consent to our Privacy Policy and agree to its terms.
        </p>
        <p>
          When you visit our site, our web servers collect and track your IP address that we later use only to analyse the usage of our website by visitors so that we can better their experience by constantly improving the quality of content on our site. We do not use IP addresses, cookies or other information that we have access to (the number of visits, average time spent, pages viewed, etc.), to trace your personal identification. Hence a user ID remains unknown or anonymous to us in this scenario with only the user session being monitored.
        </p>
        <p>
          We want you to acknowledge that the information provided by you through the 'Contact form' on the website is used only by us to respond to your inquiry, process an order or serve you with the best possible solution. Further, you should also know that occasionally, we may use your email addresses to send you the information about our company and that you can unsubscribe receiving future mailings by completing a simple request process.
        </p>
        <p>
          We want you to recognize and understand that there is no obligation on you to provide us with your name, email address or any of your personal information; and that you share your personal information with us solely by your own choice. We also want you to realise that any of your personal information that is provided to us without your consent (unless you evince so) shall be deemed to be provided by you; and that we are under no obligation to verify the source of such information.
        </p>
        <p>
          Our website may provide links to other sites (Third-Party Sites). We have no control over, do not review, and are not responsible for these Third-Party Sites, their content, or any goods or services available therein. Our Privacy Policy does not apply to Third-Party Sites, and any data you provide to Third-Party Sites, you provide at your own risk. We encourage you to review the privacy policies of any Third-Party Sites with which you choose to interact.
        </p>
        <p>
          Should you have any questions regarding our site's privacy policy, practices or your dealings with our site? Write and discuss with us.
        </p>
        <p>
          Our Site may use "cookies" to enhance user experience. Cookies allow us to collect information such as browser type, time spent on the Site, pages visited, registrations, submissions, basic demographic, and other traffic data. We use the information for security purposes, to facilitate navigation, to display information more effectively, and to personalize your experience. We may also gather statistical information about use of the Site to enhance its functionality and assist us with resolving issues internally. We may also use cookies and other technologies in online advertising campaigns to track responses to our ads.
        </p>
        <p>
          We may amend this Privacy Notice from time to time. We encourage you to check our Privacy Notice regularly to understand how we may process your Personal Data.
        </p>
        <p className="pt-4">
          Questions? Contact us at <a href="mailto:connect@blueribbontechnology.com" className="text-[#2563EB] hover:underline">connect@blueribbontechnology.com</a>
        </p>
      </div>
    </div>
  </div>
);

const TermsPage = () => (
  <div className="section-padding" data-testid="terms-page">
    <div className="container-custom max-w-3xl">
      <h1 className="font-outfit text-4xl font-bold text-[#0A2540] mb-8">Terms of Service</h1>
      <div className="prose prose-slate max-w-none">
        <p className="text-slate-600 mb-6">Last updated: March 2026</p>
        <h2 className="font-outfit text-2xl font-semibold text-[#0A2540] mt-8 mb-4">Acceptance of Terms</h2>
        <p className="text-slate-600 mb-4">
          By accessing or using Blue Ribbon Technologies Ltd. services, you agree to be bound by these Terms of Service.
        </p>
        <h2 className="font-outfit text-2xl font-semibold text-[#0A2540] mt-8 mb-4">Services</h2>
        <p className="text-slate-600 mb-4">
          Blue Ribbon Technologies provides AI solutions, digital transformation services, and consulting for small and medium businesses.
        </p>
        <h2 className="font-outfit text-2xl font-semibold text-[#0A2540] mt-8 mb-4">Intellectual Property</h2>
        <p className="text-slate-600 mb-4">
          All content on this website, including text, graphics, logos, and software, is the property of Blue Ribbon Technologies Ltd.
        </p>
        <h2 className="font-outfit text-2xl font-semibold text-[#0A2540] mt-8 mb-4">Limitation of Liability</h2>
        <p className="text-slate-600 mb-4">
          Blue Ribbon Technologies shall not be liable for any indirect, incidental, special, or consequential damages.
        </p>
        <h2 className="font-outfit text-2xl font-semibold text-[#0A2540] mt-8 mb-4">Contact</h2>
        <p className="text-slate-600">
          For questions about these Terms, contact us at connect@blueribbontechnology.com
        </p>
      </div>
    </div>
  </div>
);

export default App;
